import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import Iconify from '@/components/Iconify';
import { CUSTOM_CHAT_ENABLED } from '@/config';
import { setSelectedMenubarIconId } from '@/features/app/slices';
import { getRightSidebarMenuOptionId } from '@/features/app/utils';
import {
  getCustomChatMessages,
  openCustomChatChannel,
} from '@/features/custom-chat/api';
import {
  CUSTOM_CHAT_CHANNELS_QUERY_KEY,
  customChatChannelQueryKey,
} from '@/features/custom-chat/hooks';
import { setCustomChatActiveChannelId } from '@/features/custom-chat/slices';
import type {
  CustomChatAttachment,
  CustomChatChannel,
  CustomChatMessage,
} from '@/features/custom-chat/types';
import {
  getCustomChatMessageDisplayText,
  getCustomChatSenderRoleLabel,
  getCustomChatTranslatedMessageInfo,
} from '@/features/custom-chat/utils/messages';
import { useGetAccess } from '@/hooks/useAccess';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { dispatch } from '@/stores';
import { fDateTime } from '@/utils/formatTime';

import VBMCardWrapper from './VBMCardWrapper';
import VBMCardTitle from './partials/VBMCardTitle';

const CUSTOM_CHAT_MENU_ID = getRightSidebarMenuOptionId('custom-chat');
const SESSION_CHAT_TRANSCRIPT_MESSAGE_LIMIT = 50;

type VBMJobSupportChatTranscriptProps = {
  bookingSessionId: number;
};

export default function VBMJobSupportChatTranscript({
  bookingSessionId,
}: VBMJobSupportChatTranscriptProps) {
  const queryClient = useQueryClient();
  const chatAccess = useGetAccess('chat');
  const { errorMsg } = useSnackbarMsg();
  const canOpenChat = CUSTOM_CHAT_ENABLED && chatAccess.canView;
  const cachedJobSupportChannel = useMemo(
    () => findCachedJobSupportChannel(queryClient, bookingSessionId),
    [bookingSessionId, queryClient]
  );
  const [openedChannel, setOpenedChannel] = useState<CustomChatChannel | null>(
    cachedJobSupportChannel
  );
  const transcriptChannel = openedChannel ?? cachedJobSupportChannel;

  useEffect(() => {
    setOpenedChannel(cachedJobSupportChannel);
  }, [bookingSessionId, cachedJobSupportChannel]);

  const openChannelQuery = useQuery({
    enabled: false,
    queryFn: () =>
      openCustomChatChannel({
        booking_session_id: Number(bookingSessionId),
        type: 'jobSupport',
      }),
    queryKey: ['customChat', 'bookingSession', bookingSessionId, 'channel'],
    onError: (error) => {
      errorMsg(error, 'Unable to load session chat');
    },
    onSuccess: (channel) => {
      cacheOpenedChannel(queryClient, channel);
      setOpenedChannel(channel);
    },
    refetchOnWindowFocus: false,
    retry: false,
  });
  const messagesQuery = useQuery({
    enabled: Boolean(canOpenChat && transcriptChannel?.id),
    queryFn: () =>
      getCustomChatMessages(transcriptChannel?.id as number | string, {
        limit: SESSION_CHAT_TRANSCRIPT_MESSAGE_LIMIT,
      }),
    queryKey: [
      'customChat',
      'bookingSession',
      bookingSessionId,
      'messages',
      transcriptChannel?.id,
    ],
    refetchOnWindowFocus: false,
    retry: false,
  });
  const messages = sortTranscriptMessages(messagesQuery.data ?? []);
  const isTranscriptLoading =
    openChannelQuery.isFetching || messagesQuery.isFetching;

  const activateChannel = (channel: CustomChatChannel) => {
    cacheOpenedChannel(queryClient, channel);
    dispatch(setSelectedMenubarIconId(CUSTOM_CHAT_MENU_ID));
    dispatch(setCustomChatActiveChannelId(channel.id));
  };

  const handleOpenChat = async () => {
    if (!canOpenChat || openChannelQuery.isFetching) return;

    if (transcriptChannel) {
      activateChannel(transcriptChannel);
      return;
    }

    const { data: channel } = await openChannelQuery.refetch();
    if (channel) activateChannel(channel);
  };

  return (
    <VBMCardWrapper sx={{ p: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
      >
        <VBMCardTitle title="Session Chat" />
        <Tooltip
          title={
            canOpenChat
              ? 'Open the v2 session chat'
              : 'Chat access is not available'
          }
        >
          <span>
            <Button
              color="primary"
              disabled={!canOpenChat || openChannelQuery.isFetching}
              onClick={handleOpenChat}
              size="small"
              startIcon={<Iconify icon="eva:message-square-outline" />}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              variant="contained"
            >
              Open chat
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          mt: 1.5,
          maxHeight: 260,
          overflow: 'auto',
          p: 1.25,
        }}
      >
        {!canOpenChat ? (
          <Typography variant="body2" color="text.secondary">
            Chat access is not available.
          </Typography>
        ) : isTranscriptLoading ? (
          <Stack alignItems="center" sx={{ py: 1 }}>
            <CircularProgress size={20} />
          </Stack>
        ) : openChannelQuery.isError || messagesQuery.isError ? (
          <Typography variant="body2" color="text.secondary">
            Unable to load session chat messages.
          </Typography>
        ) : !transcriptChannel ? (
          <Typography variant="body2" color="text.secondary">
            Open chat to load session messages.
          </Typography>
        ) : messages.length ? (
          <Stack aria-label="Session chat transcript" spacing={1.25}>
            {messages.map((message) => (
              <TranscriptMessage key={message.id} message={message} />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No session chat messages yet.
          </Typography>
        )}
      </Box>
    </VBMCardWrapper>
  );
}

const cacheOpenedChannel = (
  queryClient: ReturnType<typeof useQueryClient>,
  channel: CustomChatChannel
) => {
  queryClient.setQueryData<CustomChatChannel>(
    customChatChannelQueryKey(channel.id),
    channel
  );
  queryClient.setQueryData<CustomChatChannel[]>(
    CUSTOM_CHAT_CHANNELS_QUERY_KEY,
    (previousChannels = []) => [
      channel,
      ...previousChannels.filter(
        (previousChannel) => String(previousChannel.id) !== String(channel.id)
      ),
    ]
  );
};

const findCachedJobSupportChannel = (
  queryClient: ReturnType<typeof useQueryClient>,
  bookingSessionId: number
) => {
  const channels =
    queryClient.getQueryData<CustomChatChannel[]>(
      CUSTOM_CHAT_CHANNELS_QUERY_KEY
    ) ?? [];

  return (
    channels.find((channel) => {
      if (channel.type !== 'jobSupport') return false;

      const metadataBookingSessionId = Number(
        channel.metadata?.booking_session_id ??
          channel.metadata?.bookingSessionId
      );
      const channelBookingSessionId =
        channel.booking_session_id ??
        (Number.isFinite(metadataBookingSessionId)
          ? metadataBookingSessionId
          : null);

      return Number(channelBookingSessionId) === Number(bookingSessionId);
    }) ?? null
  );
};

const TranscriptMessage = ({ message }: { message: CustomChatMessage }) => {
  const attachments = message.attachments ?? [];
  const messageText = getCustomChatMessageDisplayText(message).trim();
  const isDeleted = Boolean(message.deleted_at);
  const senderName = getTranscriptSenderName(message);
  const senderRoleLabel = getCustomChatSenderRoleLabel(message);
  const translationInfo = isDeleted
    ? null
    : getCustomChatTranslatedMessageInfo(message);
  const sentAt = formatMessageDate(message.created_at);

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Avatar
        sx={{
          bgcolor: 'primary.lighter',
          color: 'primary.dark',
          fontSize: 12,
          fontWeight: 700,
          height: 28,
          width: 28,
        }}
      >
        {getInitials(senderName)}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="baseline"
          flexWrap="wrap"
          sx={{ mb: 0.5 }}
        >
          <Typography variant="body2" fontWeight={700}>
            {senderName}
          </Typography>
          {senderRoleLabel && (
            <Chip
              icon={
                message.sender_type === 'client' ? (
                  <Iconify icon="eva:star-fill" width={13} aria-hidden />
                ) : undefined
              }
              label={senderRoleLabel}
              size="small"
              variant="outlined"
              sx={{
                color: 'text.secondary',
                fontSize: 11,
                fontWeight: 700,
                height: 20,
                '& .MuiChip-icon': {
                  color: 'warning.main',
                  ml: 0.75,
                  mr: -0.25,
                },
                '& .MuiChip-label': {
                  px: 0.75,
                },
              }}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {sentAt}
          </Typography>
          {translationInfo && (
            <Tooltip
              arrow
              placement="top"
              title={
                <Box sx={{ maxWidth: 320 }}>
                  <Typography variant="caption" fontWeight={700}>
                    {translationInfo.sourceLanguage
                      ? `Original (${translationInfo.sourceLanguage.toUpperCase()})`
                      : 'Original message'}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                  >
                    {translationInfo.originalText}
                  </Typography>
                </Box>
              }
            >
              <Box
                component="span"
                tabIndex={0}
                aria-label={`Translated message. Original: ${translationInfo.originalText}`}
                sx={{ display: 'inline-flex' }}
              >
                <Chip
                  icon={
                    <Iconify
                      icon="eva:globe-2-outline"
                      width={13}
                      aria-hidden
                    />
                  }
                  label="Translated"
                  size="small"
                  variant="outlined"
                  sx={{
                    color: 'text.secondary',
                    fontSize: 11,
                    fontWeight: 700,
                    height: 20,
                    '& .MuiChip-icon': {
                      color: 'info.main',
                      ml: 0.75,
                      mr: -0.25,
                    },
                    '& .MuiChip-label': {
                      px: 0.75,
                    },
                  }}
                />
              </Box>
            </Tooltip>
          )}
        </Stack>
        <Box
          sx={{
            bgcolor: 'background.neutral',
            borderRadius: 1,
            px: 1,
            py: 0.75,
          }}
        >
          {isDeleted ? (
            <Typography
              variant="body2"
              color="text.secondary"
              fontStyle="italic"
            >
              Message deleted
            </Typography>
          ) : (
            <>
              {messageText ? (
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {messageText}
                </Typography>
              ) : (
                !attachments.length && (
                  <Typography variant="body2" color="text.secondary">
                    No message text
                  </Typography>
                )
              )}
              {attachments.length > 0 && (
                <TranscriptAttachmentList attachments={attachments} />
              )}
            </>
          )}
        </Box>
      </Box>
    </Stack>
  );
};

const TranscriptAttachmentList = ({
  attachments,
}: {
  attachments: CustomChatAttachment[];
}) => (
  <Stack spacing={0.5} sx={{ mt: 0.75 }}>
    <Typography variant="caption" color="text.secondary" fontWeight={600}>
      {attachments.length}{' '}
      {attachments.length === 1 ? 'attachment' : 'attachments'}
    </Typography>
    {attachments.map((attachment, index) => {
      const attachmentUrl = getAttachmentUrl(attachment);
      const label = attachment.name || `Attachment ${index + 1}`;

      return attachmentUrl ? (
        <Link
          key={`${attachmentUrl}-${index}`}
          href={attachmentUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          sx={{ width: 'fit-content', wordBreak: 'break-word' }}
        >
          {label}
        </Link>
      ) : (
        <Typography
          key={`${label}-${index}`}
          variant="caption"
          color="text.secondary"
        >
          {label}
        </Typography>
      );
    })}
  </Stack>
);

const formatMessageDate = (date?: string | null) => {
  if (!date) return '';

  try {
    return fDateTime(date);
  } catch {
    return date;
  }
};

type DisplayNameSource = {
  displayName?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
  user?: DisplayNameSource | null;
};

const getTranscriptSenderName = (message: CustomChatMessage) => {
  if (
    message.sender_type === 'system' ||
    message.message_type?.toLowerCase() === 'general'
  ) {
    return 'System';
  }

  return (
    formatSenderName(message.sender_name) ||
    getPersonDisplayName(message.sender) ||
    getCustomChatSenderRoleLabel(message) ||
    'Unknown sender'
  );
};

const formatSenderName = (senderName?: string | null) => {
  const normalizedSenderName = senderName?.replace(/\s+/g, ' ').trim();

  return normalizedSenderName || null;
};

const getPersonDisplayName = (
  person?: DisplayNameSource | null
): string | null => {
  const nestedUserName =
    person?.user && person.user !== person
      ? getPersonDisplayName(person.user)
      : null;
  const fullName = [person?.first_name, person?.last_name]
    .filter(Boolean)
    .map((name) => String(name).trim())
    .filter(Boolean)
    .join(' ')
    .trim();

  return (
    fullName ||
    person?.displayName?.trim() ||
    person?.name?.trim() ||
    person?.email?.trim() ||
    nestedUserName ||
    null
  );
};

const getInitials = (name: string) => {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  return initials || '?';
};

const getAttachmentUrl = (attachment: CustomChatAttachment) =>
  isSafeExternalUrl(attachment.asset_url) ? attachment.asset_url : null;

const sortTranscriptMessages = (messages: CustomChatMessage[]) =>
  [...messages].sort((firstMessage, secondMessage) => {
    const firstTime = getMessageTimestamp(firstMessage.created_at);
    const secondTime = getMessageTimestamp(secondMessage.created_at);

    if (firstTime !== secondTime) return firstTime - secondTime;

    return String(firstMessage.id).localeCompare(String(secondMessage.id));
  });

const getMessageTimestamp = (date?: string | null) => {
  if (!date) return 0;

  const timestamp = new Date(date).getTime();

  return Number.isFinite(timestamp) ? timestamp : 0;
};

const isSafeExternalUrl = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;

  try {
    const url = new URL(value);

    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};
